/**
 * @fileoverview Onboarding wizard state management hook using Zustand.
 * Manages the complete onboarding flow including plan selection, user profile,
 * use case preferences, authentication modal state, and persistence to localStorage.
 *
 * @module hooks/useOnboardingState
 */

"use client";

import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export type PlanTier = "free" | "starter" | "pro" | "studio";
export type BillingCycle = "monthly" | "annual";
export type ModalStep = "plan" | "signup";
export type UseCase = "exterior" | "interior" | "floorplan";
export type AuthMode = "signin" | "signup";

export type OnboardingProfile = {
  name: string;
  company: string;
  role: string;
};

export type OnboardingState = {
  isModalOpen: boolean;
  modalStep: ModalStep;
  authMode: AuthMode;
  selectedPlan: PlanTier | null;
  billingCycle: BillingCycle;
  profile: OnboardingProfile;
  useCases: UseCase[];
  preferredStyle: string | null;
  onboardingComplete: boolean;
  hasSeenWelcome: boolean;
  /** URL to redirect to after successful signin/signup */
  returnTo: string | null;
  /** Referral code from URL (?ref=CODE) to apply after signup */
  referralCode: string | null;
  /** Whether the referral code has been applied to the account */
  referralApplied: boolean;
};

const initialState: OnboardingState = {
  isModalOpen: false,
  modalStep: "plan",
  authMode: "signup",
  selectedPlan: null,
  billingCycle: "monthly",
  profile: {
    name: "",
    company: "",
    role: "",
  },
  useCases: [],
  preferredStyle: null,
  onboardingComplete: false,
  hasSeenWelcome: false,
  returnTo: null,
  referralCode: null,
  referralApplied: false,
};

export type OnboardingActions = {
  openModal: () => void;
  openModalWithAuthMode: (mode: AuthMode) => void;
  closeModal: () => void;
  setModalStep: (step: ModalStep) => void;
  setAuthMode: (mode: AuthMode) => void;
  setPlan: (plan: PlanTier | null, cycle?: BillingCycle) => void;
  setBillingCycle: (cycle: BillingCycle) => void;
  setProfile: (profile: Partial<OnboardingProfile>) => void;
  setUseCases: (useCases: UseCase[]) => void;
  toggleUseCase: (useCase: UseCase) => void;
  setPreferredStyle: (style: string | null) => void;
  completeOnboarding: () => void;
  markWelcomeSeen: () => void;
  setReturnTo: (url: string | null) => void;
  setReferralCode: (code: string | null) => void;
  markReferralApplied: () => void;
  reset: () => void;
};

type OnboardingStore = OnboardingState & {
  actions: OnboardingActions;
};

const useOnboardingStore = create<OnboardingStore>()(
  persist(
    immer((set) => ({
      ...initialState,
      actions: {
        openModal: () =>
          set((state) => {
            state.isModalOpen = true;
          }),
        openModalWithAuthMode: (mode) =>
          set((state) => {
            state.isModalOpen = true;
            state.authMode = mode;
            // When opening for signin, skip plan selection
            if (mode === "signin") {
              state.modalStep = "signup";
            }
          }),
        closeModal: () =>
          set((state) => {
            state.isModalOpen = false;
          }),
        setModalStep: (step) =>
          set((state) => {
            state.modalStep = step;
          }),
        setAuthMode: (mode) =>
          set((state) => {
            state.authMode = mode;
            // Reset to plan step when switching to signup mode
            if (mode === "signup") {
              state.modalStep = "plan";
            }
          }),
        setPlan: (plan, cycle) =>
          set((state) => {
            state.selectedPlan = plan;
            if (cycle) {
              state.billingCycle = cycle;
            }
          }),
        setBillingCycle: (cycle) =>
          set((state) => {
            state.billingCycle = cycle;
          }),
        setProfile: (profile) =>
          set((state) => {
            state.profile = { ...state.profile, ...profile };
          }),
        setUseCases: (useCases) =>
          set((state) => {
            state.useCases = useCases;
          }),
        toggleUseCase: (useCase) =>
          set((state) => {
            const index = state.useCases.indexOf(useCase);
            if (index === -1) {
              state.useCases.push(useCase);
            } else {
              state.useCases.splice(index, 1);
            }
          }),
        setPreferredStyle: (style) =>
          set((state) => {
            state.preferredStyle = style;
          }),
        completeOnboarding: () =>
          set((state) => {
            state.onboardingComplete = true;
            state.isModalOpen = false;
          }),
        markWelcomeSeen: () =>
          set((state) => {
            state.hasSeenWelcome = true;
          }),
        setReturnTo: (url) =>
          set((state) => {
            state.returnTo = url;
          }),
        setReferralCode: (code) =>
          set((state) => {
            state.referralCode = code;
          }),
        markReferralApplied: () =>
          set((state) => {
            state.referralApplied = true;
          }),
        reset: () =>
          set(() => ({
            ...initialState,
          })),
      },
    })),
    {
      name: "aideco-onboarding",
      partialize: (state) => ({
        selectedPlan: state.selectedPlan,
        billingCycle: state.billingCycle,
        profile: state.profile,
        useCases: state.useCases,
        preferredStyle: state.preferredStyle,
        onboardingComplete: state.onboardingComplete,
        hasSeenWelcome: state.hasSeenWelcome,
        referralCode: state.referralCode,
        referralApplied: state.referralApplied,
        // Don't persist modal state
      }),
    },
  ),
);

/**
 * useOnboardingState manages the onboarding flow state for new users.
 * Handles plan selection, user profile, use case preferences, and modal navigation.
 *
 * @description This hook provides complete state management for user onboarding,
 * including multi-step modal navigation, plan selection with billing cycle options,
 * user profile collection, and use case preferences. State is persisted to
 * localStorage for continuity across sessions.
 *
 * @returns Object containing state, actions, and computed validation values
 * @returns {OnboardingState} return.state - Current onboarding state object
 * @returns {boolean} return.state.isModalOpen - Whether the onboarding modal is visible
 * @returns {ModalStep} return.state.modalStep - Current modal step ("plan" | "signup")
 * @returns {AuthMode} return.state.authMode - Current auth mode ("signin" | "signup")
 * @returns {PlanTier | null} return.state.selectedPlan - Selected plan tier or null
 * @returns {BillingCycle} return.state.billingCycle - Billing frequency ("monthly" | "annual")
 * @returns {OnboardingProfile} return.state.profile - User profile (name, company, role)
 * @returns {UseCase[]} return.state.useCases - Selected use cases array
 * @returns {string | null} return.state.preferredStyle - Design style preference
 * @returns {boolean} return.state.onboardingComplete - Whether onboarding is finished
 * @returns {boolean} return.state.hasSeenWelcome - Whether user has seen welcome screen
 * @returns {OnboardingActions} return.actions - Action dispatchers for state mutations
 * @returns {() => void} return.actions.openModal - Opens the onboarding modal
 * @returns {(mode: AuthMode) => void} return.actions.openModalWithAuthMode - Opens modal with specific auth mode
 * @returns {() => void} return.actions.closeModal - Closes the onboarding modal
 * @returns {(step: ModalStep) => void} return.actions.setModalStep - Sets the current modal step
 * @returns {(mode: AuthMode) => void} return.actions.setAuthMode - Sets the authentication mode
 * @returns {(plan: PlanTier | null, cycle?: BillingCycle) => void} return.actions.setPlan - Sets plan and optional billing cycle
 * @returns {(cycle: BillingCycle) => void} return.actions.setBillingCycle - Sets billing cycle
 * @returns {(profile: Partial<OnboardingProfile>) => void} return.actions.setProfile - Updates profile fields
 * @returns {(useCases: UseCase[]) => void} return.actions.setUseCases - Sets use cases array
 * @returns {(useCase: UseCase) => void} return.actions.toggleUseCase - Toggles a use case selection
 * @returns {(style: string | null) => void} return.actions.setPreferredStyle - Sets style preference
 * @returns {() => void} return.actions.completeOnboarding - Marks onboarding complete and closes modal
 * @returns {() => void} return.actions.markWelcomeSeen - Marks welcome screen as seen
 * @returns {() => void} return.actions.reset - Resets all state to initial values
 * @returns {boolean} return.isProfileComplete - True if name and role are filled
 * @returns {boolean} return.isStyleComplete - True if at least one use case is selected
 * @returns {boolean} return.shouldShowOnboarding - True if onboarding not complete
 *
 * @example Basic usage with modal control
 * ```tsx
 * const { state, actions, shouldShowOnboarding } = useOnboardingState();
 *
 * // Check if user needs onboarding
 * if (shouldShowOnboarding) {
 *   actions.openModal();
 * }
 *
 * // Close modal
 * actions.closeModal();
 * ```
 *
 * @example Plan selection flow
 * ```tsx
 * const { actions } = useOnboardingState();
 *
 * // Set plan with billing cycle
 * actions.setPlan("pro", "annual");
 *
 * // Or set separately
 * actions.setPlan("starter");
 * actions.setBillingCycle("monthly");
 * ```
 *
 * @example Profile and preferences
 * ```tsx
 * const { actions, isProfileComplete, isStyleComplete } = useOnboardingState();
 *
 * // Update profile
 * actions.setProfile({ name: "Jane Doe", company: "ACME Inc" });
 * actions.setProfile({ role: "Architect" });
 *
 * // Select use cases
 * actions.toggleUseCase("exterior");
 * actions.toggleUseCase("interior");
 *
 * // Check completion
 * if (isProfileComplete && isStyleComplete) {
 *   actions.completeOnboarding();
 * }
 * ```
 *
 * @example Auth mode switching
 * ```tsx
 * const { state, actions } = useOnboardingState();
 *
 * // Open for sign in (skips plan selection)
 * actions.openModalWithAuthMode("signin");
 *
 * // Switch to sign up (returns to plan step)
 * actions.setAuthMode("signup");
 * ```
 *
 * @see {@link OnboardingState} for complete state structure
 * @see {@link OnboardingActions} for all available actions
 * @see {@link OnboardingProfile} for profile field types
 */
export function useOnboardingState() {
  const { actions, ...state } = useOnboardingStore();

  // Always start false to prevent hydration mismatch between server/client
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    // Set hydrated immediately on mount - the persist middleware
    // synchronously rehydrates from localStorage before React renders
    setHasHydrated(true);
  }, []);

  // Profile requires name and role (company is optional per UI)
  const isProfileComplete =
    state.profile.name.trim().length > 0 && state.profile.role.trim().length > 0;

  // Style requires at least one use case selected
  const isStyleComplete = state.useCases.length > 0;

  const shouldShowOnboarding = !state.onboardingComplete;

  return {
    state,
    actions,
    isProfileComplete,
    isStyleComplete,
    shouldShowOnboarding,
    hasHydrated,
  };
}
